import * as React from 'react';
import { Component, Fragment } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Tooltip from './Tooltip';
import Translate from './Translate';
import FundFactValue from './FundFactValue';
import { ChangeIcon } from '../models/configuration/components/ChangeIcon';
import { DataStore } from '../stores';

const FundFactListItem = styled<any, any>('li')`
    ${propStyles};
`;

FundFactListItem.defaultProps = {
    flex: ['0 1 50%', '0 1 50%', '0 1 25%'],
    display: 'flex',
    minHeight: '80px',
};

const FundFact = styled<any, any>('div')`
    ${propStyles};
`;

FundFact.defaultProps = {
    flex: '1 1 100%', // Bugfix IE11 - set specific flex-basis to make long text wrap
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'md',
    margin: 'xs',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray500',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

const FundFactHeading = styled<any, any>('h3')`
    ${propStyles};
`;

FundFactHeading.defaultProps = {
    color: 'body',
    fontFamily: 'heading',
    fontWeight: 'bold',
    fontSize: 'md',
    textAlign: 'center',
    margin: '0 0 0.5em 0',
    width: '100%', // Bugfix IE11 - set specific width to make long text wrap
};

const FundFactAsAtDate = styled<any, any>('div')`
    ${propStyles};
`;

FundFactAsAtDate.defaultProps = {
    color: 'gray500',
    fontFamily: 'body',
    fontWeight: 'regular',
    fontSize: 'xs',
    margin: '0.5em 0 0 0',
};

const FundFactValueWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FundFactValueWrapper.defaultProps = {
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'primary',
    fontFamily: 'body',
    fontWeight: 'regular',
    fontSize: 'lg',
    textAlign: 'center',
};

const FundFactValueLabel = styled<any, any>('span')`
    ${propStyles}
`;

const FundFactLabelValue = styled<any, any>('div')`
    ${propStyles}
`;

interface FundFactItemProps {
    dataStore: DataStore;
    theme?: { [property: string]: string | number };
    card: FundFactCard;
    multipleValueLayout?: 'row' | 'column';
}

interface FundFactCard {
    label: string;
    asAtDate: FundFactCardAsAtDate;
    data: FundFactCardData | Array<FundFactCardData> | any; // TODO: Figure out type error for data.map, data.value without any
    multiple: boolean;
    disclaimers: FundFactDisclaimers;
    ui: 'srri' | 'stars' | 'starsOutOf';
    higherRiskLabel: string;
    lowerRiskLabel: string;
    showChange: boolean;
    changeIcon: ChangeIcon;
    noDataPlaceholder?: string;
}

interface FundFactCardData {
    value: string | number | boolean;
}

interface FundFactCardAsAtDate {
    label: string;
    value: string;
}

interface FundFactDisclaimers {
    character: string;
    content: string;
    footnote: boolean;
}

@observer
class FundFactItem extends Component<FundFactItemProps> {
    constructor(props) {
        super(props);
        FundFactValueWrapper.defaultProps.flexDirection =
            props.multipleValueLayout;
    }
    // For multiple data points return false if any of them are false
    // Otherwise return the single value
    @computed
    get value() {
        if (this.props.card.multiple) {
            return !this.props.card.data.some(d => d.value === false);
        }

        return this.props.card.data.value;
    }

    render() {
        const { theme, card, dataStore } = this.props;
        const { value } = this;
        if (value !== false) {
            return (
                <FundFactListItem {...theme.FundFactItem}>
                    <FundFact {...theme.FundFact}>
                        <FundFactHeading {...theme.FundFactHeading}>
                            <Translate>{card.label}</Translate>
                            <Tooltip
                                showContent={!card.disclaimers.footnote}
                                tooltipCharacter={card.disclaimers.character}
                                content={
                                    card.disclaimers.content
                                        ? [card.disclaimers.content]
                                        : []
                                }
                                verticalAlign="super"
                                accessibilityContext={`for disclaimers of the ${card.label} fund fact`}
                            />
                            {card.asAtDate.value && (
                                <FundFactAsAtDate {...theme.FundFactAsAtDate}>
                                    <Translate>{card.asAtDate.label}</Translate>{' '}
                                    {card.asAtDate.value}
                                </FundFactAsAtDate>
                            )}
                        </FundFactHeading>
                        <FundFactValueWrapper {...theme.FundFactValue}>
                            {card.multiple ? (
                                <Fragment>
                                    {card.data.map((data, i) => {
                                        return (
                                            <FundFactLabelValue
                                                key={i}
                                                {...theme.FundFactLabelValueWrapper}
                                            >
                                                {data.label && (
                                                    <FundFactValueLabel
                                                        {...theme.FundFactValueLabel}
                                                    >
                                                        {data.label}
                                                    </FundFactValueLabel>
                                                )}
                                                <FundFactValue
                                                    dataStore={dataStore}
                                                    ui={card.ui}
                                                    value={data.value}
                                                    separator={data.separator}
                                                    showChange={data.showChange}
                                                    higherRiskLabel={
                                                        card.higherRiskLabel
                                                    }
                                                    lowerRiskLabel={
                                                        card.lowerRiskLabel
                                                    }
                                                    alignIcon={
                                                        (data.changeIcon &&
                                                            data.changeIcon
                                                                .align) ||
                                                        (card.changeIcon &&
                                                            card.changeIcon
                                                                .align) ||
                                                        'right'
                                                    }
                                                    upIcon={
                                                        (data.changeIcon &&
                                                            data.changeIcon
                                                                .up) ||
                                                        (card.changeIcon &&
                                                            card.changeIcon.up)
                                                    }
                                                    downIcon={
                                                        (data.changeIcon &&
                                                            data.changeIcon
                                                                .down) ||
                                                        (card.changeIcon &&
                                                            card.changeIcon
                                                                .down)
                                                    }
                                                    noDataPlaceholder={
                                                        card.noDataPlaceholder
                                                    }
                                                />
                                            </FundFactLabelValue>
                                        );
                                    })}
                                </Fragment>
                            ) : (
                                <FundFactLabelValue>
                                    {card.data.label && (
                                        <FundFactValueLabel>
                                            {card.data.label}
                                        </FundFactValueLabel>
                                    )}
                                    <FundFactValue
                                        dataStore={dataStore}
                                        ui={card.ui}
                                        value={card.data.value}
                                        showChange={card.showChange}
                                        higherRiskLabel={card.higherRiskLabel}
                                        lowerRiskLabel={card.lowerRiskLabel}
                                        alignIcon={
                                            (card.changeIcon &&
                                                card.changeIcon.align) ||
                                            'right'
                                        }
                                        upIcon={
                                            card.changeIcon &&
                                            card.changeIcon.up
                                        }
                                        downIcon={
                                            card.changeIcon &&
                                            card.changeIcon.down
                                        }
                                        noDataPlaceholder={
                                            card.noDataPlaceholder
                                        }
                                    />
                                </FundFactLabelValue>
                            )}
                        </FundFactValueWrapper>
                    </FundFact>
                </FundFactListItem>
            );
        }

        return null;
    }
}

export default withTheme(FundFactItem);



// WEBPACK FOOTER //
// ./src/Components/FundFactItem.tsx